import React, { useState } from "react";
import { EVENT_TYPES, typeColors, typeInfo, fmtDate, MONTHS_PT } from "../theme";
import { I } from "../Icons";
import { PageHeader, FieldLabel, Input, Select, SectionLabel, Btn } from "../ui";

export default function EventForm({ T, dark, events, onAdd, onEdit, onDelete, onBack, isMobile }) {
  const blank = {
    date: "",
    city: "",
    state: "",
    venue: "",
    type: "CONFIRMADO",
    time: "",
    soundcheck: "",
    lat: "",
    lng: "",
    notes: ""
  };

  const [form, setForm] = useState(blank);
  const [editing, setEd] = useState(null);
  const [tab, setTab] = useState("add");
  const [delConf, setDelConf] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const today = new Date();

  const [listMonth, setListMonth] = useState(today.getMonth());
  const [listYear, setListYear] = useState(today.getFullYear());

  const px = isMobile ? 16 : 24;

  function set(k, v) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function save() {
    if (!form.date || !form.city || !form.state) {
      setErr("Data, cidade e estado sao obrigatorios.");
      return;
    }

    setErr("");
    setSaving(true);

    try {
      const payload = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null
      };

      if (editing) {
        await onEdit({ ...payload, id: editing });
        setEd(null);
      } else {
        await onAdd(payload);
      }

      setForm(blank);
    } catch (e) {
      setErr(e.message || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(ev) {
    setForm({
      ...ev,
      lat: ev.lat || "",
      lng: ev.lng || ""
    });

    setEd(ev.id);
    setTab("add");
  }

  function prevMonth() {
    if (listMonth === 0) {
      setListMonth(11);
      setListYear(y => y - 1);
    } else {
      setListMonth(m => m - 1);
    }
  }

  function nextMonth() {
    if (listMonth === 11) {
      setListMonth(0);
      setListYear(y => y + 1);
    } else {
      setListMonth(m => m + 1);
    }
  }

  function goToday() {
    const now = new Date();
    setListMonth(now.getMonth());
    setListYear(now.getFullYear());
  }

  function handleMonthChange(e) {
    setListMonth(Number(e.target.value));
  }

  function handleYearChange(e) {
    setListYear(Number(e.target.value));
  }

  /*
   * Anos disponíveis:
   * - inclui o ano atual
   * - inclui todos os anos existentes nos eventos
   * - permite navegar pelo histórico
   */
  const eventYears = events
    .map(ev => Number(String(ev.date || "").slice(0, 4)))
    .filter(y => Number.isFinite(y) && y > 1900 && y < 2200);

  const years = Array.from(
    new Set([
      today.getFullYear(),
      ...eventYears
    ])
  ).sort((a, b) => b - a);

  /*
   * Lista filtrada pelo mês/ano selecionado.
   */
  const filteredEvents = [...events]
    .filter(ev => {
      if (!ev.date) return false;

      const date = new Date(ev.date + "T12:00:00");

      return (
        date.getFullYear() === listYear &&
        date.getMonth() === listMonth
      );
    })
    .sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;

      return (a.time || "").localeCompare(b.time || "");
    });

  const currentMonthCount = filteredEvents.length;

  const inp = {
    T,
    style: { marginBottom: 4 }
  };

  const lbl = { T };

  return (
    <div style={{ paddingBottom: 60 }}>

      {/* DELETE CONFIRM */}
      {delConf && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: T.modalBg,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24
          }}
          onClick={() => setDelConf(null)}
        >
          <div
            style={{
              background: T.surface,
              borderRadius: 14,
              padding: 24,
              width: "100%",
              maxWidth: 320,
              boxShadow: `0 8px 32px ${T.shadowMd}`
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 15,
                color: T.text,
                marginBottom: 8
              }}
            >
              Remover show?
            </div>

            <div
              style={{
                fontSize: 13,
                color: T.textSub,
                marginBottom: 20
              }}
            >
              Esta acao nao pode ser desfeita.
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Btn
                T={T}
                variant="secondary"
                style={{ flex: 1 }}
                onClick={() => setDelConf(null)}
              >
                Cancelar
              </Btn>

              <Btn
                T={T}
                variant="danger"
                style={{ flex: 1 }}
                onClick={async () => {
                  await onDelete(delConf);
                  setDelConf(null);
                }}
              >
                Remover
              </Btn>
            </div>
          </div>
        </div>
      )}

      <PageHeader
        T={T}
        onBack={onBack}
        title="Gerenciar Shows"
        px={px}
      />

      {/* TABS */}
      <div
        style={{
          display: "flex",
          background: T.surfaceAlt,
          borderBottom: `1px solid ${T.border}`
        }}
      >
        {["add", "list"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flex: 1,
              padding: "12px 0",
              background: "transparent",
              border: "none",
              borderBottom: `2px solid ${
                tab === t ? T.accent : "transparent"
              }`,
              color: tab === t ? T.accent : T.textSub,
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
              letterSpacing: 0.5
            }}
          >
            {t === "add"
              ? editing
                ? "EDITAR"
                : "NOVO SHOW"
              : `LISTA (${events.length})`}
          </button>
        ))}
      </div>

      {/* NOVO / EDITAR */}
      {tab === "add" ? (
        <div style={{ padding: `0 ${px}px 24px` }}>

          {editing && (
            <div
              style={{
                background: T.notifNew,
                border: `1px solid ${T.notifBorder}`,
                borderRadius: 8,
                padding: "8px 12px",
                margin: "12px 0 0",
                fontSize: 12,
                color: T.text
              }}
            >
              Editando show. Salve para confirmar as alteracoes.
            </div>
          )}

          {err && (
            <div
              style={{
                background: "rgba(211,47,47,0.1)",
                border: `1px solid ${T.danger}`,
                borderRadius: 8,
                padding: "8px 12px",
                margin: "12px 0 0",
                fontSize: 12,
                color: T.danger
              }}
            >
              {err}
            </div>
          )}

          <SectionLabel T={T}>Data e Hora</SectionLabel>

          <FieldLabel T={T}>Data *</FieldLabel>

          <Input
            {...inp}
            type="date"
            value={form.date}
            onChange={e => set("date", e.target.value)}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10
            }}
          >
            <div>
              <FieldLabel T={T}>Horario do show</FieldLabel>

              <Input
                {...inp}
                type="time"
                value={form.time}
                onChange={e => set("time", e.target.value)}
              />
            </div>

            <div>
              <FieldLabel T={T}>Passagem de som</FieldLabel>

              <Input
                {...inp}
                type="time"
                value={form.soundcheck}
                onChange={e => set("soundcheck", e.target.value)}
              />
            </div>
          </div>

          <SectionLabel T={T}>Local</SectionLabel>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 72px",
              gap: 10
            }}
          >
            <div>
              <FieldLabel T={T}>Cidade *</FieldLabel>

              <Input
                {...inp}
                placeholder="SAO PAULO"
                value={form.city}
                onChange={e =>
                  set("city", e.target.value.toUpperCase())
                }
              />
            </div>

            <div>
              <FieldLabel T={T}>Estado *</FieldLabel>

              <Input
                {...inp}
                placeholder="SP"
                maxLength={2}
                value={form.state}
                onChange={e =>
                  set("state", e.target.value.toUpperCase())
                }
              />
            </div>
          </div>

          <FieldLabel T={T}>Venue / Local</FieldLabel>

          <Input
            {...inp}
            placeholder="Nome do espaco"
            value={form.venue}
            onChange={e => set("venue", e.target.value)}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10
            }}
          >
            <div>
              <FieldLabel T={T}>Latitude (opcional)</FieldLabel>

              <Input
                {...inp}
                placeholder="-23.5505"
                value={form.lat}
                onChange={e => set("lat", e.target.value)}
              />
            </div>

            <div>
              <FieldLabel T={T}>Longitude (opcional)</FieldLabel>

              <Input
                {...inp}
                placeholder="-46.6333"
                value={form.lng}
                onChange={e => set("lng", e.target.value)}
              />
            </div>
          </div>

          <p
            style={{
              fontSize: 11,
              color: T.textMuted,
              marginTop: 2,
              marginBottom: 0
            }}
          >
            Sem coordenadas, o Maps buscara pelo nome do local.
          </p>

          <SectionLabel T={T}>Tipo</SectionLabel>

          <FieldLabel T={T}>Tipo do evento</FieldLabel>

          <Select
            T={T}
            value={form.type}
            onChange={e => set("type", e.target.value)}
          >
            {EVENT_TYPES.map(t => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </Select>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              margin: "4px 0"
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: 5,
                background: typeColors(form.type, dark).bg
              }}
            />

            <span
              style={{
                fontSize: 11,
                color: T.textSub
              }}
            >
              {typeInfo(form.type).label}
            </span>
          </div>

          <FieldLabel T={T}>Observacoes</FieldLabel>

          <textarea
            rows={3}
            value={form.notes}
            onChange={e => set("notes", e.target.value)}
            placeholder="Informacoes extras..."
            style={{
              width: "100%",
              background: T.inputBg,
              border: `1.5px solid ${T.inputBorder}`,
              borderRadius: 9,
              padding: "11px 13px",
              fontSize: 14,
              color: T.text,
              outline: "none",
              resize: "vertical",
              marginBottom: 4
            }}
          />

          <div
            style={{
              display: "flex",
              gap: 10,
              marginTop: 16
            }}
          >
            {editing && (
              <Btn
                T={T}
                variant="secondary"
                style={{ flex: 1 }}
                onClick={() => {
                  setEd(null);
                  setForm(blank);
                  setErr("");
                }}
              >
                Cancelar
              </Btn>
            )}

            <Btn
              T={T}
              variant="primary"
              style={{ flex: 2 }}
              onClick={save}
              disabled={saving}
            >
              {saving
                ? "SALVANDO..."
                : editing
                  ? "SALVAR ALTERACOES"
                  : "ADICIONAR SHOW"}
            </Btn>
          </div>
        </div>

      ) : (

        /* ============================================================
           LISTA DE SHOWS
           ============================================================ */

        <div style={{ paddingBottom: 20 }}>

          {/* CABEÇALHO DE NAVEGAÇÃO */}
          <div
            style={{
              background: T.surface,
              padding: `12px ${px}px 10px`,
              borderBottom: `1px solid ${T.border}`
            }}
          >

            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: T.textMuted,
                letterSpacing: 2,
                textTransform: "uppercase",
                marginBottom: 8
              }}
            >
              Lista de shows
            </div>

            {/* NAVEGAÇÃO */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8
              }}
            >

              <button
                onClick={prevMonth}
                style={{
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "7px 10px",
                  cursor: "pointer",
                  color: T.text,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                title="Mes anterior"
              >
                <I n="Left" size={16} />
              </button>

              <div
                style={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr 100px" : "1fr 120px",
                  gap: 8
                }}
              >

                {/* MES */}
                <select
                  value={listMonth}
                  onChange={handleMonthChange}
                  style={{
                    width: "100%",
                    background: T.inputBg,
                    border: `1px solid ${T.inputBorder}`,
                    borderRadius: 8,
                    padding: "8px 10px",
                    color: T.text,
                    fontSize: 13,
                    fontWeight: 700,
                    outline: "none",
                    cursor: "pointer"
                  }}
                >
                  {MONTHS_PT.map((name, index) => (
                    <option key={index} value={index}>
                      {name}
                    </option>
                  ))}
                </select>

                {/* ANO */}
                <select
                  value={listYear}
                  onChange={handleYearChange}
                  style={{
                    width: "100%",
                    background: T.inputBg,
                    border: `1px solid ${T.inputBorder}`,
                    borderRadius: 8,
                    padding: "8px 10px",
                    color: T.text,
                    fontSize: 13,
                    fontWeight: 700,
                    outline: "none",
                    cursor: "pointer"
                  }}
                >
                  {years.map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>

              </div>

              <button
                onClick={nextMonth}
                style={{
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "7px 10px",
                  cursor: "pointer",
                  color: T.text,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                title="Proximo mes"
              >
                <I n="Right" size={16} />
              </button>

            </div>

            {/* SEGUNDA LINHA */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginTop: 9
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: T.textMuted
                }}
              >
                {currentMonthCount === 1
                  ? "1 show"
                  : `${currentMonthCount} shows`}
              </span>

              <button
                onClick={goToday}
                style={{
                  background: "none",
                  border: "none",
                  color: T.accent,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: "4px 0"
                }}
              >
                IR PARA HOJE
              </button>
            </div>

          </div>

          {/* LISTA FILTRADA */}
          <div style={{ padding: `12px ${px}px` }}>

            {filteredEvents.length === 0 ? (

              <div
                style={{
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 11,
                  padding: 32,
                  textAlign: "center"
                }}
              >
                <I
                  n="Cal"
                  size={28}
                  style={{
                    color: T.textMuted,
                    marginBottom: 8
                  }}
                />

                <p
                  style={{
                    color: T.textMuted,
                    margin: 0,
                    fontSize: 14
                  }}
                >
                  Nenhum show em {MONTHS_PT[listMonth]} {listYear}.
                </p>
              </div>

            ) : (

              filteredEvents.map(ev => {
                const { bg, text } = typeColors(ev.type, dark);

                return (
                  <div
                    key={ev.id}
                    style={{
                      background: bg,
                      borderRadius: 11,
                      padding: "11px 13px",
                      marginBottom: 10,
                      position: "relative",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                    }}
                  >

                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: text,
                        opacity: 0.8,
                        paddingRight: 70
                      }}
                    >
                      {fmtDate(ev.date)}
                      {ev.time ? ` · ${ev.time}` : ""}
                      {ev.soundcheck ? ` · SC ${ev.soundcheck}` : ""}
                    </div>

                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: text,
                        marginTop: 2,
                        paddingRight: 70
                      }}
                    >
                      {ev.city}/{ev.state}
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color: text,
                        opacity: 0.8,
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        marginTop: 2,
                        paddingRight: 70
                      }}
                    >
                      <I n="Pin" size={11} />
                      {ev.venue}
                    </div>

                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: text,
                        opacity: 0.6,
                        marginTop: 3,
                        letterSpacing: 0.5
                      }}
                    >
                      {typeInfo(ev.type).label}
                    </div>

                    {/* AÇÕES */}
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        display: "flex",
                        gap: 5
                      }}
                    >
                      <button
                        onClick={() => startEdit(ev)}
                        style={{
                          background: "rgba(0,0,0,0.18)",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          padding: "5px 7px",
                          color: text,
                          display: "flex"
                        }}
                        title="Editar"
                      >
                        <I n="Edit" size={13} />
                      </button>

                      <button
                        onClick={() => setDelConf(ev.id)}
                        style={{
                          background: "rgba(0,0,0,0.18)",
                          border: "none",
                          borderRadius: 6,
                          cursor: "pointer",
                          padding: "5px 7px",
                          color: text,
                          display: "flex"
                        }}
                        title="Excluir"
                      >
                        <I n="Trash" size={13} />
                      </button>
                    </div>

                  </div>
                );
              })

            )}

          </div>
        </div>
      )}
    </div>
  );
}
